<template>
  <!-- Main Header - Hidden on Login Page -->
  <header v-if="shouldShowHeader">
    <div :class="[headerPadding]">
      <v-container
        class="d-flex justify-space-between align-center ga-2 header-container"
      >
        <!-- Left Side: Logo, Navigation, Title -->
        <div class="d-flex align-center valign-wrapper right-side">
          <!-- Logo -->
          <img
            v-if="shouldShowLogo"
            class="siteLogo"
            :src="logoUrl"
            alt="logo image"
            @click="gotoHomePage"
          />

          <!-- Back Button -->
          <div v-if="shouldShowBackButton" class="back-btn">
            <v-icon
              @click="$router.push('/navigationPage')"
              icon="icon-back mdi-arrow-right"
            />
          </div>

          <!-- Page Title -->
          <div v-if="shouldShowTitle" class="title">
            {{ pageTitle }}
          </div>

          <!-- Navigation Cards (Desktop Only) -->
          <div v-if="shouldShowNavCards" class="nav-cards">
            <NavCard
              :title="$t('Header.controlPanel')"
              :subTitle="$t('Header.pendingItems')"
              :amount="openAgentRequests"
              mode="controlPanel"
              iconName="mdi-face-agent"
              refi=""
              :shortWidth="true"
            />

            <NavCard
              :title="$t('Header.tommorowOrders')"
              :subTitle="$t('Header.openTommorowOrders')"
              :amount="tomorrowOrdersAmount"
              mode="controlPanel"
              iconName="mdi-invoice-clock-outline"
              refi="tomorrowOrders/tommorowcustomers"
              :shortWidth="true"
            />

            <NavCard
              :title="$t('Header.myorders')"
              :subTitle="$t('Header.activeorders')"
              :amount="openOrders"
              mode="orders"
              iconName="mdi-clipboard-text-clock"
              :refi="ordersRefi"
              :shortWidth="true"
            />

            <NavCard
              :title="$t('Header.cunstomers')"
              :subTitle="$t('Header.activeCustomers')"
              :amount="activeCustomers"
              mode="customers"
              iconName="mdi-account-group-outline"
              refi="customers/allcustomers"
              :shortWidth="true"
            />

            <NavCard
              v-if="user?.canSeeAgentsSales"
              :title="$t('Header.agentSales')"
              :subTitle="$t('Header.agentSalesSubTitle')"
              :amount="'-'"
              mode="customers"
              iconName="mdi-flag-checkered"
              refi="AgentSales"
              :shortWidth="true"
            />

            <NavCard
              title="פתיחת לקוח"
              subTitle="טופס פתיחת לקוח"
              :amount="'-'"
              mode="catalog"
              iconName="mdi-account-plus-outline"
              refi=""
              :shortWidth="true"
              @card-click="openCustomerDevelopmentForm"
            />

            <NavCard
              title="קטלוג"
              subTitle="עיון בקטלוג"
              :amount="'-'"
              mode="catalog"
              iconName="mdi-book-open-variant"
              refi=""
              :shortWidth="true"
              @card-click="isCatalogOpen = true"
            />
          </div>
        </div>

        <!-- User Section -->
        <div class="left-align hide-on-small-only">
          <div v-if="isAuth">
            <div class="d-flex flex-column align-end">
              <div class="d-flex ga-4 align-center">
                <v-btn
                  v-if="getUserRole?.customerapp"
                  icon="mdi-cog-outline"
                  variant="text"
                  density="comfortable"
                  title="הגדרות מנהל"
                  @click="openAdminPanel"
                />
                <div class="user-short-details">
                  <!-- <p>{{ customerName }}</p> -->
                  <p>{{ $t("Header.hello") }}, {{ username }}!</p>

                  <a
                    v-if="canSwitchAgent"
                    @click.prevent="isOpenSelectAgentModel = true"
                    href=""
                    class="link-text"
                  >
                    {{ $t("Header.switchAgentProfile") }}
                  </a>

                  <a
                    v-if="canShowLogout"
                    @click.prevent="logOutUser"
                    href=""
                    class="link-text"
                  >
                    <span v-if="hasMultipleAgents"> | </span>
                    {{ $t("Header.logout") }}
                  </a>
                </div>
              </div>
              <div class="lang-container"></div>
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Agent Selection Dialog -->
    <AgentSelectionDialog
      :isLoading="isLoading"
      :persistent="isDialogPersistent"
      v-model="isOpenSelectAgentModel"
      :user-agents="filteredAgents || []"
      @agent-selected="selectAgent"
    />

    <!-- Catalog Fullscreen Dialog -->
    <v-dialog v-model="isCatalogOpen" fullscreen :scrim="false" transition="dialog-bottom-transition">
      <v-card class="catalog-dialog">
        <v-toolbar density="compact" color="white">
          <v-toolbar-title>קטלוג</v-toolbar-title>
          <v-spacer />
          <v-btn icon="mdi-close" @click="isCatalogOpen = false" />
        </v-toolbar>
        <div class="catalog-iframe-wrapper">
          <iframe
            v-if="isCatalogOpen"
            class="catalog-iframe"
            src="https://heyzine.com/flip-book/07bbde8f91.html"
            allowfullscreen
            allow="fullscreen"
          />
        </div>
      </v-card>
    </v-dialog>

    <!-- Admin Panel (customer app) Fullscreen Dialog -->
    <v-dialog v-model="isAdminPanelOpen" fullscreen class="iframe-modal">
      <v-card color="black">
        <v-btn variant="text" flat icon @click="isAdminPanelOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-card-text class="pa-0">
          <iframe
            v-if="isAdminPanelOpen"
            :src="adminPanelUrl"
            frameborder="0"
            style="width: 100%; height: 100%"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Loading Indicator - Top Bar -->
    <v-progress-linear
      v-if="isLoading"
      color="#801512"
      :height="3"
      indeterminate
      class="loading-top-bar"
    />
  </header>
</template>

<script>
import NavCard from "../helpers/NavCard";
import AgentSelectionDialog from "../helpers/AgentSelectionDialog";
import AuthService from "@/services/AuthService";
import { getQueryPayLoad } from "../../utilities";
import { mapGetters, mapActions } from "vuex";
import config from "@/config";
import SiteService from "@/services/SiteService";

export default {
  name: "TheHeader",

  components: {
    NavCard,
    AgentSelectionDialog,
  },

  data() {
    return {
      userOption: "",
      locale: this.$i18n.locale,
      cartT: this.cartTotal,
      isOpenSelectAgentModel: false,
      isDialogPersistent: false,
      isCatalogOpen: false,
      isAdminPanelOpen: false,
      adminPanelUrl: "",
    };
  },

  methods: {
    // ===========================
    // Agent Management Methods
    // ===========================

    async selectAgent(agent) {
      this.loading = true;
      this.isDialogPersistent = true;

      const response = await this.$store.dispatch("AuthModule/setAgent", {
        agent: agent,
        userPhoneNumber: this.$store.getters["AuthModule/getUser"]?.phoneNumber,
      });

      if (response) {
        await AuthService.getBigData();

        // Refresh data based on current route
        if (this.isHomePage) {
          await this.$store.dispatch("ControlPanel/agentsList");
          await this.$store.dispatch("ControlPanel/distLinesList");
        }

        if (this.currentRouteName === "orders") {
          await AuthService.getBigData(
            "orders",
            getQueryPayLoad(this.$route.query, this.$route.path)
          );
        }

        if (
          this.currentRouteName === "customers" ||
          this.currentRouteName === "TomorrowOrders"
        ) {
          await AuthService.getBigData(
            "customers",
            getQueryPayLoad(this.$route.query, this.$route.path)
          );
        }

        this.isOpenSelectAgentModel = false;
        this.loading = false;
        this.isDialogPersistent = false;
      } else {
        this.loading = false;
        this.isDialogPersistent = false;
        this.$router.push({ name: "loginPage" });
      }
    },

    // ===========================
    // Authentication Methods
    // ===========================

    async logOutUser() {
      try {
        await this.logout();
      } catch (error) {
        console.log(error);
      } finally {
        window.location.reload(true);
      }
    },

    // ===========================
    // Navigation Methods
    // ===========================

    gotoHomePage() {
      this.$store.commit("setIsShowDesktopHome", false);
      if (this.isDesktop) {
        this.$router.push("/");
      } else {
        this.$router.push({ name: "NavigationPage" });
      }
    },

    openMobileSideBar() {
      this.$store.commit("setIsOpenMobileSideBar", true);
    },

    async openCustomerDevelopmentForm() {
      // open before the await so popup blockers allow it
      const win = window.open("", "_blank");
      const { data } = await SiteService.getCustomerDevelopmentUrl().catch(() => ({}));
      win.location = data?.url || config.customerDevelopmentUrl;
    },

    // same one-time passkey login as "הזמנה חדשה", landing on the admin panel
    async openAdminPanel() {
      const response = await this.$store.dispatch("Orders/newOrder", {});
      if (response) {
        this.adminPanelUrl = config.adminPanelUrl(
          response.passkey,
          this.getUserPhone
        );
        this.isAdminPanelOpen = true;
      }
    },

    // ===========================
    // Store Actions
    // ===========================

    ...mapActions({
      logout: "AuthModule/logout",
    }),
  },
  computed: {
    // ===========================
    // Device & Layout Computed Properties
    // ===========================

    isMobile() {
      return this.$store.getters.isMobile;
    },

    isTablet() {
      return this.$store.getters.isTablet;
    },

    isLaptop() {
      return this.$store.getters.isLaptop;
    },

    isDesktop() {
      return this.$store.getters.isDesktop;
    },

    isMobileOrTabletOrLaptop() {
      return this.isMobile || this.isTablet || this.isLaptop;
    },

    isDesktopOnly() {
      return !this.isMobileOrTabletOrLaptop;
    },

    // ===========================
    // Route & Navigation Computed Properties
    // ===========================

    currentRouteName() {
      return this.$route.name;
    },

    isLoginPage() {
      return this.currentRouteName === "loginPage";
    },

    shouldShowHeader() {
      return !this.isLoginPage;
    },

    isHomePage() {
      return this.currentRouteName === "home";
    },

    isNavigationPage() {
      return this.currentRouteName === "NavigationPage";
    },

    isHomeOrNavigationPage() {
      return this.isHomePage || this.isNavigationPage;
    },

    isInternalPage() {
      return !this.isHomeOrNavigationPage;
    },

    // ===========================
    // Logo Display Logic
    // ===========================

    shouldShowLogoForDesktop() {
      return this.isDesktopOnly && this.isAuth;
    },

    shouldShowLogoForMobile() {
      return this.isHomeOrNavigationPage;
    },

    shouldShowLogo() {
      return this.shouldShowLogoForDesktop || this.shouldShowLogoForMobile;
    },

    shouldShowBackButton() {
      return (
        this.isMobileOrTabletOrLaptop && this.isAuth && this.isInternalPage
      );
    },

    shouldShowTitle() {
      return this.isInternalPage && this.isMobileOrTabletOrLaptop;
    },

    shouldShowNavCards() {
      return this.isDesktopOnly;
    },

    // ===========================
    // User & Auth Computed Properties
    // ===========================

    username() {
      if (this.user?.agents?.length > 0) {
        return this.user?.agents?.filter(
          (agent) => agent.agent === this.user.agent
        )[0].agentname;
      }
      return this.userName;
    },

    filteredAgents() {
      if (this.isOpenSelectAgentModel && this.user?.agents?.length > 0) {
        return this.user?.agents?.filter(
          (agent) => agent.agent !== this.user.agent
        );
      }
      return [];
    },

    hasMultipleAgents() {
      return this.user?.agents?.length > 0;
    },

    canSwitchAgent() {
      return this.isAuth && this.hasMultipleAgents;
    },

    canShowLogout() {
      return this.isAuth;
    },

    // ===========================
    // Navigation Data Computed Properties
    // ===========================

    tomorrowOrdersAmount() {
      return `${this.getCustomersWithTommorowDistLine}/${this.getCustomersMadeOrderForTommorowDistLineCount}`;
    },

    ordersRefi() {
      return `orders/allorders?mainStatus=2&orderDate=${new Date().toISOString()}`;
    },

    pageTitle() {
      return this.$t(`sideBar.${this.currentRouteName}.title`);
    },

    // ===========================
    // Layout Computed Properties
    // ===========================

    headerPadding() {
      if (this.isDesktop || this.isHomeOrNavigationPage) {
        return "pt-5 pb-5";
      } else if (this.isTablet || this.isMobile) {
        return "pt-2 pb-2";
      }
      return "pt-2 pb-2";
    },

    logoUrl() {
      return this.$t("Header.logourl");
    },
    // ===========================
    // Store Getters
    // ===========================

    ...mapGetters({
      isAuth: "AuthModule/isAuthenticated",
      user: "AuthModule/getUser",
      isUserRollFinance: "AuthModule/isUserRollFinance",
      userName: "AuthModule/getUserName",
      customerName: "AuthModule/getCustomerName",
      openOrders: "Orders/getOpenOrders",
      activeCustomers: "Customers/getActiveCustomers",
      getCustomersMadeOrderForTommorowDistLineCount:
        "Customers/getCustomersMadeOrderForTommorowDistLineCount",
      getCustomersWithTommorowDistLine:
        "Customers/getCustomersWithTommorowDistLine",
      openAgentRequests: "AgentRequests/getOpenAgentRequests",
      isLoading: "isLoading",
      getUserRole: "AuthModule/getUserRole",
      getUserPhone: "AuthModule/getUserPhone",
    }),
  },

  // ===========================
  // Watchers
  // ===========================

  watch: {
    $route(to) {
      if (to.name !== "loginPage") {
        AuthService.updateHeaderCounters();
      }
    },

    locale(val) {
      this.$i18n.locale = val;
      document.dir = this.$i18n.messages[this.$i18n.locale].direction;
      document.documentElement.lang = this.$i18n.locale;
      this.$vuetify.locale.current = this.$i18n.locale;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/main.scss";

// ===========================
// Loading Indicator Styles
// ===========================

.loading-top-bar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  z-index: 1000 !important;
}

// ===========================
// Header Container Styles
// ===========================

header {
  box-shadow: 0px 2px 8px #00000010;
  z-index: 2 !important;
  background-color: white;

  .container {
    padding: 10px 0 5px 0px;
  }

  .header-container {
    top: unset;
  }

  .v-container {
    margin: 0px !important;
    max-width: 100% !important;

    @include media(mobile) {
      // min-height: 90px;
    }
  }

  @include media(mobile) {
    box-shadow: unset;
    position: unset;
    padding: 0;
  }
}

// ===========================
// Catalog Dialog Styles
// ===========================

.catalog-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;

  .catalog-iframe-wrapper {
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
  }

  .catalog-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
}

// ===========================
// Logo Styles
// ===========================

.siteLogo {
  height: 87px;
  width: auto;
  margin-left: 45px;
  cursor: pointer;

  &:lang(en) {
    margin: unset;
    margin-right: 45px;

    @include media(mobile) {
      margin-right: 10px;
    }
  }

  &.favicon {
    @include media(mobile) {
      height: 87px;
      width: 110px;
      padding: 5px;
    }
  }

  @include media(tablet) {
    margin-left: 10px;
  }
}

// ===========================
// Navigation Elements
// ===========================

.back-btn {
  @include media(smallLaptop, laptop, tablet, mobile) {
    background: linear-gradient(
      235.26deg,
      #6d1106 0%,
      #801512 40.5%,
      #992a26 94%,
      #a33e3a 100%
    );
    width: 66px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-left: 10px;
    border-radius: 50px 0px 0px 50px;

    .icon-back {
      color: white;
    }
  }
}

.title {
  @include media(smallLaptop, laptop, tablet) {
    color: $statusListColor;
    margin-right: 7px;
    font-weight: 500;
    font-size: $largTextSize;
  }
  @include media(mobile) {
    font-size: $smallTextSize !important;
  }
}

.nav-cards {
  display: flex;
  flex-wrap: nowrap;
}

// ===========================
// User Details Section
// ===========================

.user-short-details {
  text-align: left;

  p {
    &:nth-child(1) {
      font-weight: 500;
      font-size: $smallTextSize;
    }
  }

  @include media(mobile, tablet, smallLaptop) {
    display: flex;
    gap: 10px;
  }
}

.user-icon {
  padding: 15px;
  background-color: $primaryBackgroundColor;
  border-radius: 15px;
}

// ===========================
// Layout Helper Classes
// ===========================

.right-side {
  @include media(mobile) {
    justify-content: space-between !important;
    padding-bottom: 0px !important;

    .v-col {
      padding-bottom: 0px !important;
    }
  }

  @include media(tablet) {
    padding-bottom: 0px !important;
  }
}

.valign-wrapper {
  @include media(tablet) {
    justify-content: flex-end;
  }
}

// ===========================
// Cart Related Styles
// ===========================

.cart-icon {
  @include media(mobile) {
    background: $controlPanelMainColor;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    margin-left: 20px;
    box-shadow: 0px 4px 4px 0px #00000040;
  }
}

.cart-button {
  @include media(mobile) {
    position: relative;
  }
}

.product-in-cart {
  @include media(mobile) {
    position: absolute;
    top: -9px;
    right: -5px;
    background: $customerMainColor;
    color: white;
    padding: 0px 6px;
    font-size: 14px;
    z-index: 100;
    border-radius: 50%;
  }
}

// ===========================
// Navigation Card Styles
// ===========================

.orders-nav-card {
  @include media(mobile) {
    margin-right: 0px;
  }
}

.cart-nav-card {
  @include media(mobile) {
    margin-left: 5px;

    &:lang(en) {
      margin-left: 0px;
      margin-right: 5px;
    }
  }
}

// ===========================
// Avatar & UI Components
// ===========================

.v-avatar {
  border-radius: 15px;
}

.option-selected-conteiner {
  position: -webkit-sticky;
  position: sticky;
  top: 92.59px;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px;
  background-color: $primaryColorlight;

  span {
    font-size: $mediumTextSize;
  }
}

// ===========================
// Language & Text Styles
// ===========================

.lang-container a {
  text-decoration: none;
  font-size: $smallTextSize;
}

.link-text {
  text-decoration: none;
  color: $hrefLinkColor !important;
}

.language-icon {
  margin: 0;
  margin-left: 5px;
}

// ===========================
// Button & Link Styles
// ===========================

a {
  font-size: $smallTextSize;
  vertical-align: unset;
  color: $primaryTextColor !important;
}

.btn.dropdown-trigger {
  padding: unset;
  background-color: transparent;
  color: #1c1c1c;
  box-shadow: unset;
}

.material-icons.left {
  margin-right: 5px;
}

// ===========================
// Utility Classes
// ===========================

.progress {
  position: absolute;
}

.col {
  padding: unset !important;
}

ul {
  padding: unset;

  li {
    a {
      text-align: center;
    }
  }
}

#header-container-left {
  justify-self: left;
}
</style>
